import { publicFile } from '@shared/helpers/public-file.helper';
import { Task } from '@shared/models/task.model';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { TaskType } from '@shared/enums/task-type.enum';
import { Project } from '@modules/projects/shared/models/project.model';

export type TaskMetaType = 'scheduled' | 'sent' | 'completed' | 'cancelled';

export interface TaskMetaDetails {
  type: TaskMetaType;
  date: string;
  labelKey: string;
  actorName: string;
  actorAvatar: string | null;
  isCancelled: boolean;
}

export const PROCESS_TYPE_TRANSLATION_MAP: Record<string, string> = {
  task: 'ProjectsDashboard.stageTasks.taskTypes.task',
  documentation: 'ProjectsDashboard.stageTasks.taskTypes.documentation',
  finance: 'ProjectsDashboard.stageTasks.taskTypes.finance',
  control: 'ProjectsDashboard.stageTasks.taskTypes.control',
  message: 'ProjectsDashboard.stageTasks.taskTypes.message',
  follow_up: 'Tasks.TaskType.follow_up',
  appointment: 'Tasks.TaskType.appointment'
};

export const TASK_TYPE_CLASS_MAP: Record<string, string> = {
  task: 'type-task',
  note: 'type-note',
  order: 'type-order',
  correction: 'type-correction',
  change: 'type-change',
  documentation: 'type-documentation',
  finance: 'type-finance',
  control: 'type-control',
  message: 'type-message',
  follow_up: 'type-follow-up',
  appointment: 'type-appointment'
};

export const TASK_TYPE_ICON_MAP: Record<string, string> = {
  task: 'done',
  note: 'sticky_note_2',
  order: 'shopping_cart',
  correction: 'construction',
  change: 'build',
  documentation: 'photo_camera',
  finance: 'euro_symbol',
  control: 'search',
  message: 'mail_outline',
  follow_up: 'sync',
  appointment: 'event'
};

export const TASK_STATUS_ICON_MAP: Record<string, string> = {
  [TaskStatus.CREATED]: 'check',
  [TaskStatus.COMPLETED]: 'check',
  [TaskStatus.CANCELLED]: 'close'
};

export const TASK_STATUS_CLASS_MAP: Record<string, string> = {
  [TaskStatus.CREATED]: 'status-created',
  [TaskStatus.COMPLETED]: 'status-completed',
  [TaskStatus.CANCELLED]: 'status-cancelled'
};

export function normalizeTaskStatus(status: TaskStatus | string | null | undefined): TaskStatus {
  if (status === TaskStatus.COMPLETED) return TaskStatus.COMPLETED;
  if (status === TaskStatus.CANCELLED || status === 'cancelled' || status === 'canceled') {
    return TaskStatus.CANCELLED;
  }
  return TaskStatus.CREATED;
}

export function getNormalizedTaskType(type: string | null | undefined): string {
  const taskType = String(type || '');
  return taskType === TaskType.TYPE_COMPANY_TASK || taskType === TaskType.TYPE_CLIENT_TASK
    ? TaskType.TYPE_TASK
    : taskType;
}

export function getTaskTypeTranslationKey(type: string | null | undefined, isProcessGroup: boolean): string {
  const normalizedType = getNormalizedTaskType(type);
  if (!normalizedType) return '';
  if (!isProcessGroup) return `Tasks.TaskType.${normalizedType}`;
  return PROCESS_TYPE_TRANSLATION_MAP[normalizedType] || `Tasks.TaskType.${normalizedType}`;
}

export function getTaskTypeCssClass(type: string | null | undefined): string {
  const normalizedType = getNormalizedTaskType(type);
  return normalizedType ? TASK_TYPE_CLASS_MAP[normalizedType] || '' : '';
}

export function getTaskTypeIcon(type: string | null | undefined): string {
  return TASK_TYPE_ICON_MAP[getNormalizedTaskType(type)] || 'done';
}

export function getTaskStatusIcon(status: TaskStatus | string | null | undefined): string {
  return TASK_STATUS_ICON_MAP[normalizeTaskStatus(status)] || 'check';
}

export function getTaskStatusCssClass(status: TaskStatus | string | null | undefined): string {
  return TASK_STATUS_CLASS_MAP[normalizeTaskStatus(status)] || 'status-created';
}

export function isMessageLikeTaskType(type: string | null | undefined): boolean {
  const normalizedType = String(type || '');
  return normalizedType === TaskType.TYPE_MESSAGE || normalizedType === TaskType.TYPE_FOLLOW_UP;
}

export function hasVisibleSubtasks(task: Task | null | undefined): boolean {
  return !!((task?.childrenCount || 0) > 0 || (task?.children || []).some((child) => !child?.deleted));
}

export function getProjectMetaProject(task: Task | null | undefined): Project | null {
  const taskProject = task?.project;
  return taskProject?.id ? new Project(taskProject) : null;
}

export function getProjectMetaPrimaryLine(project: Project | null): string {
  if (!project) return '';

  return String(project.fullNameAndAddress || project.clientNameWithAddress || project.fullAddress || '').trim();
}

export function getProjectMetaSecondaryLine(project: Project | null, primaryLine: string): string {
  if (!project) return '';

  const value = String(project.nameAndProjectID || project.projectNameWithID || project.name || '').trim();
  return value !== primaryLine ? value : '';
}

export function getProjectMetaInlineLabel(primaryLine: string, secondaryLine: string): string {
  return [primaryLine, secondaryLine].filter(Boolean).join(' | ');
}

export function getPersonDisplayName(person: any): string {
  const fullName = String(person?.fullName || '').trim();
  if (fullName) return fullName;

  const userPerson = person?.userPerson || {};
  const previewName = String(userPerson?.previewName || '').trim();
  if (previewName) return previewName;

  const userPersonFullName = String(userPerson?.fullName || '').trim();
  if (userPersonFullName) return userPersonFullName;

  const fromUserPerson = [userPerson?.firstName, userPerson?.lastName].filter(Boolean).join(' ').trim();
  if (fromUserPerson) return fromUserPerson;

  return [person?.name, person?.surname].filter(Boolean).join(' ').trim();
}

export function getPersonAvatar(person: any): string | null {
  return publicFile(person?.userPerson?.photo, 100);
}

export function getStatusMetaDate(task: Task | null | undefined): string | null {
  return task?.statusChangedAt || task?.completedDate || task?.modified || null;
}

export function getStatusMetaLabelKey(status: string | null | undefined): string {
  const normalizedStatus = String(status || '');
  if (normalizedStatus === TaskStatus.COMPLETED) return 'Tasks.taskCompletedAt';
  if (normalizedStatus === TaskStatus.CANCELLED || normalizedStatus === 'cancelled') {
    return 'Tasks.taskCanceledAt';
  }
  return '';
}

export function getLatestTaskMeta(task: Task | null | undefined): TaskMetaDetails | null {
  if (!task) return null;

  const normalizedStatus = normalizeTaskStatus(task.status);
  const statusMetaDate = getStatusMetaDate(task);
  const statusMetaType =
    normalizedStatus === TaskStatus.CANCELLED ? ('cancelled' as const) : ('completed' as const);

  const items = [
    {
      type: 'scheduled' as const,
      date: task.mailScheduledAt || null,
      labelKey: 'Tasks.messageScheduledAt',
      actorName: getPersonDisplayName(task.mailSentBy),
      actorAvatar: getPersonAvatar(task.mailSentBy),
      isCancelled: false
    },
    {
      type: 'sent' as const,
      date: task.mailSentAt || null,
      labelKey: 'Tasks.messageSentAt',
      actorName: getPersonDisplayName(task.mailSentBy),
      actorAvatar: getPersonAvatar(task.mailSentBy),
      isCancelled: false
    },
    {
      type: statusMetaType,
      date:
        normalizedStatus === TaskStatus.COMPLETED || normalizedStatus === TaskStatus.CANCELLED
          ? statusMetaDate
          : null,
      labelKey: getStatusMetaLabelKey(normalizedStatus),
      actorName: getPersonDisplayName(task.statusChangedBy),
      actorAvatar: getPersonAvatar(task.statusChangedBy),
      isCancelled: statusMetaType === 'cancelled'
    }
  ]
    .map((item) => ({
      ...item,
      timestamp: getMetaTimestamp(item.date)
    }))
    .filter((item) => item.timestamp !== null)
    .sort((a, b) => Number(b.timestamp) - Number(a.timestamp));

  const latest = items[0];
  if (!latest?.date) return null;

  return {
    type: latest.type,
    date: latest.date,
    labelKey: latest.labelKey,
    actorName: latest.actorName,
    actorAvatar: latest.actorAvatar,
    isCancelled: latest.isCancelled
  };
}

function getMetaTimestamp(date: string | null | undefined): number | null {
  if (!date) return null;
  const timestamp = new Date(date).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}
